<script lang="ts">
	import { Background } from "@components/Background.js";
	import BookMeeting from "@components/BookMeeting.svelte";
	import FaqItems from "@components/FaqItems.svelte";
	import InfoCard from "@components/InfoCard.svelte";
	import LogoReferences from "@components/LogoReferences.svelte";
	import Ol from "@components/Ol.svelte";
	import { OlSize } from "@components/OlProps.js";
	import ReferenceContents from "@components/ReferenceContents.svelte";
	import Section from "@components/Section.svelte";
	import Formatted from "@components/common/Formatted.svelte";
	import Icon from "@components/common/Icon.svelte";
	import Link from "@components/common/Link.svelte";
	import { ButtonVariant } from "@components/common/getButtonClass.js";
	import Layout from "@components/layout/Layout.svelte";
	import arrowNext from "@core/assets/icons/arrow-next.svg?raw";
	import { PartnerPlacement } from "@core/schema/PartnerPlacement.js";
	import { ReferencePlacement } from "@core/schema/ReferencePlacement.js";
	import { getClientsPath } from "@core/schema/paths/getClientsPath.js";
	import { getConnectWithFastestPath } from "@core/schema/paths/getConnectWithFastestPath.js";
	import { getDevelopmentPath } from "@core/schema/paths/getDevelopmentPath.js";
	import { getReferencePath } from "@core/schema/paths/getReferencePath.js";
	import { getGlobalTemplateProps } from "../contexts/getGlobalTemplateProps.js";
	import { BookMeetingPerson, getBookMeetingPeople } from "../data/getBookMeetingPeople.js";
	import { getFaqsTransport } from "../data/getFaqsTransport.js";
	import { getPartnerLogos } from "../data/getPartnerLogos.js";
	import { getSolvedProblemsTransport } from "../data/getSolvedProblemsTransport.js";
	import { getTransportWhyFastest } from "../data/getTransportWhyFastest.js";
	import type { TransportProps } from "./TransportProps.js";

	const { tenant } = getGlobalTemplateProps();
	const { partners }: TransportProps = $props();
</script>

<Layout withStructuredData>
	<Section background={Background.LightGradient} centered gridHeadline="container-6" gridDesc="container-6" hero>
		{#snippet headline()}
			<Formatted text="Co kdybyste za cg[[stejný čas]] odbavili cg[[více]] zásilek?" />
		{/snippet}
		{#snippet desc()}
			<p>
				<Formatted
					text="sp[[Logistickým]] a sp[[spedičním]] firmám nebo dopravcům (nejen) pomocí [{getDevelopmentPath(
						tenant,
					)}][[softwaru na míru]] pomáháme sp[[sjednotit]] řízení podniku pod sp[[jeden systém]], odbavit za stejný čas sp[[více zásilek]], sp[[skoncovat]] s papírovými „camráky“ nebo sp[[zjednodušit]] plánování práce řidičů."
				/>
			</p>
		{/snippet}
		{#snippet cta()}
			<Link
				variant={ButtonVariant.PhloxOutlineLightBg}
				class="flex items-center justify-center mx-auto mt-2"
				href={getConnectWithFastestPath(tenant)}
			>
				<Formatted text="Nezávazně poptat" />
				<Icon icon={arrowNext} class="ml-2.5 text-fstgrey-900 w-5"></Icon>
			</Link>
		{/snippet}
	</Section>

	<Section
		gridHeadline="container-12 md:container-8 xl:container-start-2 xl:container-end-5"
		gridDesc="container-12 md:container-8 xl:container-start-7 xl:container-end-10"
		paddingFollowNext
	>
		{#snippet headline()}
			<Formatted text="cg[[Zapomeňte]] na hromadění „camráků“ i cg[[zmatky]] v systémech" />
		{/snippet}
		{#snippet desc()}
			<p>
				<Formatted
					text="Kdo dnes dělá přepravu postaru, sp[[těžko uspěje]] v našlapané konkurenci. Tým Fastest vás sp[[zbaví hromadění papírových tzv. camráků]], používání několika sp[[chaotických systémů najednou]] nebo sp[[zmatků v pojištění vozidel]] i sp[[neefektivního plánování nakládek a vykládek.]]"
				/>
			</p>
			<p>
				<Formatted
					text="Výsledkem budou sp[[desítky hodin ušetřeného času]], takže za stejný čas odbavíte několikanásobně víc zásilek."
				/>
			</p>
		{/snippet}
	</Section>

	<Section
		background={Background.LightGradient}
		paddingFollowNext
		gridHeadline="container-8"
		gridDesc="container-6"
		centered
	>
		{#snippet headline()}
			<Formatted text="S čím za námi cg[[přepravní firmy]] často přichází" />
		{/snippet}
		{#snippet desc()}{/snippet}
	</Section>
	<Section gridChildren="max-md:full-width" background={Background.White} noPadding="both">
		<div
			class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-1 max-lg:full-width lg:px-1 bg-fstlight-100 border-t-4 border-fstlight-100"
		>
			{#each getSolvedProblemsTransport(tenant) as { title, desc }, index (index)}
				<InfoCard {title} {desc} indexNumber={index + 1} />
			{/each}
		</div>
	</Section>
	<Link
		variant={ButtonVariant.Phlox}
		class="mx-auto flex items-center justify-center mt-8 lg:mt-12"
		href={getConnectWithFastestPath(tenant)}
		>Zeptat se na podrobnosti
		<Icon icon={arrowNext} class="ml-2 text-fstgrey-900"></Icon>
	</Link>
	<Section
		gridHeadline="container-12 md:container-8 xl:container-start-2 xl:container-end-5"
		gridDesc="container-12 md:container-8 xl:container-start-7 xl:container-end-10"
	>
		{#snippet headline()}
			<Formatted text="cg[[Software na míru]] pro logistické a spediční firmy i dopravce" />
		{/snippet}
		{#snippet desc()}
			<p>
				<Formatted
					text="Kde se šustne zásilka, tam sp[[umíme pomoci]] se zefektivněním nebo digitalizací procesů. Proto sp[[pomáháme firmám]] napříč přepravou."
				/>
			</p>
			<div class="flex flex-col md:flex-row md:gap-16">
				<ul class="custom-list">
					<li><Formatted text="Spediční firmy" /></li>
					<li><Formatted text="Logistické firmy" /></li>
					<li><Formatted text="Dopravci" /></li>
					<li><Formatted text="Kurýrní služby" /></li>
					<li><Formatted text="Provozovatelé úschovných boxů" /></li>
					<li><Formatted text="Poskytovatelé skladových služeb" /></li>
				</ul>
			</div>
		{/snippet}
	</Section>

	<Section gridHeadline="container-8" centered>
		{#snippet headline()}
			<Formatted text="Systém na cg[[doručování zboží]] do boxů pro OX Point" />
		{/snippet}
		<div class="mt-12">
			<ReferenceContents limit={8} placement={ReferencePlacement.Transport} />
		</div>
		<Link
			variant={ButtonVariant.GreenOutlineLightBg}
			class="mx-auto flex items-center justify-center mt-8 lg:mt-12"
			href={getReferencePath(tenant)}
			>Zobrazit více
			<Icon icon={arrowNext} class="ml-2 text-fstgrey-900"></Icon>
		</Link>
	</Section>

	<Section background={Background.LightGradient} centered gridHeadline="container-6" noPadding="bot">
		{#snippet headline()}
			<Formatted text="Komu dalšímu jsme cg[[pomohli?]]" />
		{/snippet}
	</Section>
	<LogoReferences logos={getPartnerLogos(partners, PartnerPlacement.Transport)} />

	<Section
		paddingFollowNext
		gridHeadline="container-12 md:container-8 xl:container-start-2 xl:container-end-5"
		gridDesc="container-12 md:container-8 xl:container-start-7 xl:container-end-10"
		gridChildren="container-12"
	>
		{#snippet headline()}
			<Formatted text="cg[[5 důvodů]], proč s námi přepravní firmy cg[[spolupracují]]" />
		{/snippet}
		{#snippet desc()}
			<div class="flex flex-col gap-8">
				<Ol items={getTransportWhyFastest(tenant)} size={OlSize.Medium} />
			</div>
		{/snippet}

		<Link
			variant={ButtonVariant.Phlox}
			class="mx-auto flex items-center justify-center mt-8 lg:mt-12"
			href={getConnectWithFastestPath(tenant)}
			>Chci fastest služby
			<Icon icon={arrowNext} class="ml-2 text-fstgrey-900"></Icon>
		</Link>
	</Section>

	<Section centered gridHeadline="container-6">
		{#snippet headline()}
			<Formatted text="Často kladené otázky k řešením v oblasti cg[[přepravy]]" />
		{/snippet}
		<FaqItems faqs={getFaqsTransport(tenant)} />
	</Section>

	{@const contacts = getBookMeetingPeople(tenant)}
	<BookMeeting contacts={[contacts[BookMeetingPerson.Kucera], contacts[BookMeetingPerson.Svejda]]} />
</Layout>
